/**
 * AI Center — Phase 1.5 v7.3
 *
 * Catalogue des assistants IA. Mais surtout : le but est que l'IA soit
 * intégrée aux workflows métier. Cette page est un ANNEAU de jonction.
 *
 * Couleur domaine : AI = #8B5CF6 violet.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { Brain, Pill, FileText, Activity, Sparkles, ArrowRight, Zap, Bot } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader, KpiRow } from "@/components/PageHeader";
import { DOMAINS } from "@/lib/design-system";
import { trackCenter } from "@/lib/track-center";
import { useEffect } from "react";

export const Route = createFileRoute("/ai/")({
  component: AICenter,
});

function AICenter() {
  const d = DOMAINS.ai;

  useEffect(() => {
    trackCenter("ai");
  }, []);
  const assistants = [
    { to: "/ai/icd10-advisor", label: "ICD-10 Advisor", sub: "Recherche sémantique de codes CIM-10", icon: Brain, kpi: "47 usages / 7j" },
    { to: "/ai/drug-interactions", label: "Drug Interactions", sub: "Vérification automatique des prescriptions", icon: Pill, kpi: "23 vérifs / 7j" },
    { to: "/ai/clinical-notes", label: "Clinical Notes", sub: "Génération de notes SOAP", icon: FileText, kpi: "12 notes / 7j" },
    { to: "/ai/predictive", label: "Predictive Analytics", sub: "Risque admission + sepsis + réadmission", icon: Activity, kpi: "Bientôt" },
  ];

  const topKpis = [
    { label: "Suggestions IA (7j)", value: 47, delta: +31, intent: "good" as const, icon: Brain, sparkline: [20, 25, 30, 35, 40, 44, 47] },
    { label: "Taux d'acceptation", value: "82%", delta: +5, intent: "good" as const, icon: Zap, sparkline: [70, 73, 75, 78, 80, 81, 82] },
    { label: "Vector index size", value: "18 codes", delta: 0, intent: "neutral" as const, icon: Sparkles, sparkline: [18, 18, 18, 18, 18, 18, 18] },
    { label: "Models en prod", value: 3, delta: 0, intent: "neutral" as const, icon: Bot, sparkline: [3, 3, 3, 3, 3, 3, 3] },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 space-y-4">
      <PageHeader
        title="AI Center"
        description="Assistants IA pour les workflows cliniques · l'IA est intégrée dans Consultation, pas dans un menu à part"
        domain="ai"
        actions={
          <Badge className={d.badge}>
            <Sparkles className="h-3 w-3 mr-1" /> Upstash Vector HYBRID
          </Badge>
        }
      >
        <KpiRow items={topKpis} domain="ai" />
      </PageHeader>

      <Card className={`bg-slate-900/60 border-slate-800 hover:${d.borderClass} transition`}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className={`h-4 w-4 ${d.textClass}`} />
            Assistants disponibles
          </CardTitle>
          <p className="text-xs text-slate-500">Chaque assistant est aussi accessible directement depuis son workflow métier (ex. ICD-10 Advisor dans Consultation).</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {assistants.map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.to} to={a.to} className="group">
                <div className={`bg-slate-950/60 hover:${d.bgClass} transition rounded-lg p-4 border border-slate-800 group-hover:border-violet-500/30`}>
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-lg ${d.bgClass} ${d.textClass} flex items-center justify-center`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{a.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{a.sub}</div>
                      <div className="text-[10px] text-violet-400 mt-2 font-mono">{a.kpi}</div>
                    </div>
                    <ArrowRight className={`h-4 w-4 ${d.textClass} opacity-0 group-hover:opacity-100 transition`} />
                  </div>
                </div>
              </Link>
            );
          })}
        </CardContent>
      </Card>

      <Card className={`bg-slate-900/60 border-slate-800 hover:${d.borderClass} transition`}>
        <CardHeader>
          <CardTitle className="text-base">Intégration dans le workflow Consultation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <FlowStep label="Symptômes" desc="Saisie médecin" />
            <ArrowRight className="h-3 w-3 text-slate-500" />
            <FlowStep label="IA Suggestion" desc="Top 5 codes CIM-10" highlight />
            <ArrowRight className="h-3 w-3 text-slate-500" />
            <FlowStep label="ICD-10" desc="Validation médecin" />
            <ArrowRight className="h-3 w-3 text-slate-500" />
            <FlowStep label="Prescription" desc="INN + Drug-check" />
            <ArrowRight className="h-3 w-3 text-slate-500" />
            <FlowStep label="Validation" desc="Signature + audit" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FlowStep({ label, desc, highlight }: { label: string; desc: string; highlight?: boolean }) {
  return (
    <div className={`px-3 py-2 rounded-lg border ${highlight ? "bg-violet-500/10 border-violet-500/30" : "bg-slate-950/40 border-slate-800"}`}>
      <div className={`text-xs font-semibold ${highlight ? "text-violet-300" : "text-slate-200"}`}>{label}</div>
      <div className="text-[10px] text-slate-500">{desc}</div>
    </div>
  );
}